import React, { useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import {
    ArrowRightAltRounded,
    ContentCopyRounded,
    DeleteRounded,
    EditRounded,
    LockOpenFilledRounded,
    NotificationsActiveRounded,
    NotificationAddRounded,
    ReadMoreRounded,
    RestoreFromTrashRounded,
    UploadRounded
} from '@mui-symbols-material/w300';
import { ActionItemConfig } from '@dreebit-registry/vsm-microservice-components/dist/components/ActionsMenu';
import { useUserResources } from '@dreebit-registry/vsm-microservice-components';
import { InstalledBaseNode, ManualEventType } from '../../codegen/graphql';
import NodeAccessModal from './nodeAccessModal';
import DeleteNodeModal from './DeleteNodeModal';
import CreateInstalledBaseEventModal from '../CreateInstalledBaseEventModal';
import CsvStructureImportModal from './csvStructureImportModal';
import NodeSubscriptionModal from '../NodeSubscriptionModal';
import DuplicateNodeWizardModal from '../DuplicateNodeWizard/DuplicateNodeWizardModal';
import MoveInstalledBaseNodeModal, { MoveInstalledBaseNodeSuccessInfo } from '../MoveInstalledBaseNodeModal';
import { InstalledBaseNodeSubscriptions } from '../../graphql/queries/installedBaseNodeSubscriptions';
import { InstalledBaseIotReportSubscriptions } from '../../graphql/queries/installedBaseIotReportSubscriptions';
import { isNodeInUserPool } from '../../utils/installedBasePath';

// 'detailPanel' mirrors 'header' but omits Subscribe — the node detail panel
// shows a dedicated subscription bell next to its actions menu, unlike the
// column header, where the menu is the only place to reach it.
export type NodeActionContext = 'header' | 'listRow' | 'detailPanel';

export interface NodeActionConfig extends ActionItemConfig {
    contexts: NodeActionContext[];
}

interface UseNodeActionsArgs {
    targetNode: InstalledBaseNode;
    isRoot: boolean;
    context: NodeActionContext;
    /** True when the target node lies inside the trash (see isNodeInTrash) —
     *  moving it out is a restore, so the Move action is relabeled. */
    inTrash?: boolean;
    /** True when the target node lies inside a User Pool — for callers whose
     *  node source doesn't select pathNodes (list rows). Otherwise derived from
     *  the target node itself. */
    inUserPool?: boolean;
    refetchFunction?: () => void;
    deleteNodeCallback?: (deletedNodeId: string) => void;
    moveNodeCallback?: (info: MoveInstalledBaseNodeSuccessInfo) => void;
    /** Opens the node detail panel on its Information tab, which embeds the
     *  edit form (IBM-1087: no edit modal in the tree). Without it — e.g. the
     *  root column header, which has no target node — the Edit action is hidden. */
    onEditNode?: () => void;
}

export const useNodeActions = ({
    targetNode,
    isRoot,
    context,
    inTrash,
    inUserPool,
    refetchFunction,
    deleteNodeCallback,
    moveNodeCallback,
    onEditNode,
}: UseNodeActionsArgs): { items: ActionItemConfig[]; modals: React.ReactNode } => {
    const { t } = useTranslation();
    const resources = useUserResources();

    const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
    const [accessModalOpen, setAccessModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [assetDeleteModalOpen, setAssetDeleteModalOpen] = useState(false);
    const [csvImportModalOpen, setCsvImportModalOpen] = useState(false);
    const [moveAssetModalOpen, setMoveAssetModalOpen] = useState(false);
    const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);

    const access = targetNode?.access;
    const nodeType = targetNode?.nodeType;
    const targetId = targetNode?.id;
    const parentNodeId = targetNode?.parentNode?.id;

    // Client root = no parent AND an owner client. The owner guard also covers
    // node sources that don't select parentNode (owner is only selected for
    // root rows and column-header nodes).
    const iotReportClientId =
        targetNode?.parentNode == null && targetNode?.owner?.id
            ? String(targetNode.owner.id)
            : undefined;

    const { data: subscriptionsData } = useQuery(InstalledBaseNodeSubscriptions, {
        fetchPolicy: 'cache-first',
    });

    const { data: reportSubscriptionsData } = useQuery(InstalledBaseIotReportSubscriptions, {
        fetchPolicy: 'cache-first',
        skip: !iotReportClientId,
    });

    const isAlreadySubscribed = useMemo(() => {
        if (!targetId) return false;
        const subs = (subscriptionsData?.installedBaseNodeSubscriptionList?.subscriptions ?? []) as any[];
        if (subs.some((sub) => sub?.installedBaseNode?.id === targetId)) return true;

        // Client roots also count as subscribed when an IoT report
        // subscription exists for the client (managed in the same modal).
        if (!iotReportClientId) return false;
        const reportSubs = (reportSubscriptionsData?.installedBaseIotReportSubscriptionList?.subscriptions ?? []) as any[];
        return reportSubs.some((sub) => sub?.client?.id === iotReportClientId);
    }, [subscriptionsData, reportSubscriptionsData, targetId, iotReportClientId]);

    const canWrite = (isRoot && resources.includes('installedBaseRootNodeUpdate')) || (!isRoot && !!access?.write);

    const canDelete = nodeType?.key !== 'TRASH'
        && ((isRoot && resources.includes('installedBaseRootNodeDelete')) || (!isRoot && !!access?.delete));

    // An installed asset cannot be deleted via deleteInstalledBaseNode — it has to
    // leave its location through an ASSET_DELETE lifecycle event. Parked assets
    // (isInstalled false: pools, trash) take the regular delete form instead, which
    // keeps the audit trail free of synthetic removal events.
    const isInstalledAsset =
        nodeType?.key === 'ASSET' && targetNode?.assetLocation?.isInstalled === true;

    // IBM-586: the TRASH node itself offers no actions at all, and elements inside
    // the trash only Restore (= Move) and Delete — everything else waits until the
    // element is restored.
    const isTrashNode = nodeType?.key === 'TRASH';
    const allowGeneralActions = !isTrashNode && !inTrash;

    // A User Pool holds assets only; the backend rejects a CSV structure import
    // there, so it is not offered at all.
    const userPool = !!inUserPool || isNodeInUserPool(targetNode);

    // Protected nodes (companies, system pools, the trash) are never moveable — the
    // header menu can target a CLIENT node even though it isn't the root column, so
    // gate by type.
    const canMove = !isRoot
        && nodeType?.key !== 'CLIENT'
        && nodeType?.key !== 'IMPORT_POOL'
        && nodeType?.key !== 'USER_POOL'
        && nodeType?.key !== 'TRASH'
        && !!targetId
        && !!access?.write;

    const all: NodeActionConfig[] = [
        {
            // Without write access the same entry reads "Details" — the panel's
            // form is disabled then. Inside the panel itself that would be a
            // no-op, so there it stays a write-only action.
            contexts: ['header', 'listRow', 'detailPanel'],
            item: (
                <Box display="flex" alignItems="center">
                    {canWrite
                        ? <EditRounded fontSize="inherit" sx={{ mr: '4px' }} />
                        : <ReadMoreRounded fontSize="inherit" sx={{ mr: '4px' }} />
                    }
                    {canWrite ? t('Edit') : t('Details')}
                </Box>
            ),
            onClick: () => onEditNode?.(),
            active: !!onEditNode
                && !!targetId
                && allowGeneralActions
                && (canWrite || context !== 'detailPanel'),
        },
        {
            // Duplicates are created as siblings, so the parent node id is required;
            // it is only part of the list-row node data (header node lacks parentNode).
            contexts: ['listRow'],
            item: (
                <Box display="flex" alignItems="center">
                    <ContentCopyRounded fontSize="inherit" sx={{ mr: '4px' }} />{t('Duplicate')}
                </Box>
            ),
            onClick: () => setDuplicateModalOpen(true),
            active: !isRoot
                && nodeType?.key !== 'ASSET'
                && nodeType?.key !== 'CLIENT'
                && allowGeneralActions
                && !!parentNodeId
                && !!access?.write,
        },
        {
            contexts: ['header', 'listRow', 'detailPanel'],
            item: (
                // Moving an element out of the trash IS the restore (there is no
                // dedicated restore mutation) — present it as such.
                <Box display="flex" alignItems="center">
                    {inTrash
                        ? <RestoreFromTrashRounded fontSize="inherit" sx={{ mr: '4px' }} />
                        : <ArrowRightAltRounded fontSize="inherit" sx={{ mr: '4px' }} />
                    }
                    {inTrash ? t('Restore') : t('Move')}
                </Box>
            ),
            onClick: () => setMoveAssetModalOpen(true),
            active: canMove,
        },
        {
            contexts: ['header', 'listRow', 'detailPanel'],
            item: (
                <Box display="flex" alignItems="center">
                    <LockOpenFilledRounded fontSize="inherit" sx={{ mr: '4px' }} />{t('Access')}
                </Box>
            ),
            onClick: () => setAccessModalOpen(true),
            active: resources.includes('administrateAccessGroups') && allowGeneralActions,
        },
        {
            // Deliberately NOT in 'detailPanel' — the panel has its own bell.
            contexts: ['header', 'listRow'],
            item: (
                <Box display="flex" alignItems="center">
                    {isAlreadySubscribed
                        ? <NotificationsActiveRounded fontSize="inherit" sx={{ mr: '4px' }} />
                        : <NotificationAddRounded fontSize="inherit" sx={{ mr: '4px' }} />
                    }
                    {isAlreadySubscribed
                        ? t('Edit subscription')
                        : t('Subscribe')
                    }
                </Box>
            ),
            onClick: () => setSubscriptionModalOpen(true),
            active: !!targetId && allowGeneralActions,
        },
        {
            contexts: ['header', 'listRow', 'detailPanel'],
            item: (
                <Box display="flex" alignItems="center">
                    <UploadRounded fontSize="inherit" sx={{ mr: '4px' }} />{t('Import')}
                </Box>
            ),
            onClick: () => setCsvImportModalOpen(true),
            active: allowGeneralActions && !userPool,
        },
        {
            contexts: ['header', 'listRow', 'detailPanel'],
            item: (
                <Box display="flex" alignItems="center">
                    <DeleteRounded fontSize="inherit" sx={{ mr: '4px' }} />{t('Delete')}
                </Box>
            ),
            onClick: () => (isInstalledAsset ? setAssetDeleteModalOpen(true) : setDeleteModalOpen(true)),
            active: canDelete,
        },
    ];

    const items: ActionItemConfig[] = all
        .filter((a) => a.contexts.includes(context))
        .map(({ contexts, ...rest }) => rest);

    const modals = (
        <>
            {duplicateModalOpen && targetId && parentNodeId && nodeType?.key && (
                <DuplicateNodeWizardModal
                    open={duplicateModalOpen}
                    onClose={() => setDuplicateModalOpen(false)}
                    template={{
                        templateNodeId: targetId,
                        templateTitle: targetNode.title ?? '',
                        nodeTypeKey: nodeType.key,
                        parentNodeId,
                    }}
                    refetchFunction={refetchFunction}
                />
            )}
            {accessModalOpen && (
                <NodeAccessModal
                    open={accessModalOpen}
                    onClose={() => setAccessModalOpen(false)}
                    node={targetNode}
                />
            )}
            {deleteModalOpen && targetId && (
                <DeleteNodeModal
                    open={deleteModalOpen}
                    onClose={() => setDeleteModalOpen(false)}
                    nodeId={targetId}
                    node={targetNode}
                    refetchFunction={refetchFunction}
                    deleteNodeCallback={deleteNodeCallback}
                />
            )}
            {assetDeleteModalOpen && targetId && targetNode?.assetLocation?.asset?.id && (
                <CreateInstalledBaseEventModal
                    assetId={targetNode.assetLocation.asset.id}
                    assetLocationId={targetNode.assetLocation.id}
                    installedBaseNodeId={targetId}
                    open={assetDeleteModalOpen}
                    onClose={() => setAssetDeleteModalOpen(false)}
                    initialEventType={ManualEventType.ASSET_DELETE}
                    // Deleting moves the asset's node into the trash, so beyond the
                    // modal's default asset refetches the tree columns need refreshing.
                    refetchQueries={[
                        "InstalledBaseNodeList",
                        "InstalledBaseRootNodeList",
                        "InstalledBaseNode",
                        "AssetBreadcrumbPath",
                        "AssetNameplateInfo",
                        "InstalledBaseEventList",
                    ]}
                    onSuccess={() => {
                        refetchFunction?.();
                        deleteNodeCallback?.(targetId);
                    }}
                />
            )}
            {csvImportModalOpen && (
                <CsvStructureImportModal
                    open={csvImportModalOpen}
                    onClose={() => setCsvImportModalOpen(false)}
                    node={targetNode}
                    refetchFunction={refetchFunction}
                />
            )}
            {subscriptionModalOpen && targetId && (
                <NodeSubscriptionModal
                    open={subscriptionModalOpen}
                    onClose={() => setSubscriptionModalOpen(false)}
                    installedBaseNodeId={targetId}
                    iotReportClientId={iotReportClientId}
                />
            )}
            {moveAssetModalOpen && targetId && (
                <MoveInstalledBaseNodeModal
                    open={moveAssetModalOpen}
                    onClose={() => setMoveAssetModalOpen(false)}
                    nodeId={targetId}
                    titleText={inTrash ? t('Restore node') : undefined}
                    onMoved={(info) => {
                        refetchFunction?.();
                        moveNodeCallback?.(info);
                    }}
                />
            )}
        </>
    );

    return { items, modals };
};
